<template>
  <div class="container mt-0 pt-0">
    <div class="options-block">
      <div v-for="(faqCategory, index) in faqsCategories" :key="index">
        <hr class="mb-2" v-if="index !== 0" />
        <Accordion
          :title="faqCategory.title"
          :title-style="accordionStyle"
          :default-open="faqFiltered"
          :enable-animation="!faqFiltered"
          :icon-type="iconType"
        >
          <div class="options-block">
            <div v-for="(item, i) in faqCategory.questions" :key="i">
              <Accordion
                :title="item"
                :description="faqCategory.answers[i]"
                @onClick="gtagEventOpenQuestion(item)"
              >
                <div
                  v-if="shouldShowWithdrawalCalculator(faqCategory, i)"
                  class="mt-3"
                >
                  <p class="fw-bold my-0 text-black">
                    {{ t("CL_HELP_WITHDRAWAL_CALCULATOR_AD") }}
                  </p>
                  <div
                    @click="$emit('redirectToCalculator')"
                    class="text-decoration-none"
                  >
                    <span id="item-link">
                      {{ t("CL_HELP_WITHDRAWAL_CALCULATOR_LINK") }}
                    </span>
                  </div>
                </div>
                <div class="mt-3">
                  <p
                    class="green"
                    @click="$emit('cancelCreditLine')"
                    v-if="showCancelCreditLine(faqCategory, i)"
                  >
                    {{ t("CANCELCREDITLINE") }}
                  </p>
                </div>
              </Accordion>
              <hr
                class="mt-2 mb-2"
                v-if="i !== faqCategory.questions.length - 1"
              />
            </div>
          </div>
        </Accordion>
      </div>
    </div>
    <p v-if="useFaqSettings" class="text-muted text-center mt-4" style="font-size: 0.75rem;">FAQ v2</p>
  </div>
</template>

<script lang="ts">
import {
  computed,
  defineComponent,
  onMounted,
  onUnmounted,
  PropType,
  ref,
} from "vue";
import Accordion from "@/apps/credit-line/components/Accordion.vue";
import { useI18n } from "@/apps/credit-line/composables/useI18n";
import logAnalyticsEvent from "@/apps/credit-line/services/google-analytics-events.service";
import AnalyticEvents from "@/shared-helpers/google-analytics-events.helper";
import useAsset from "@/apps/credit-line/helpers/useAsset";
import { getCountryFromI18nLocale } from "../../helpers/locale-mapping.helper";
import { FaqsHelper } from "@/apps/credit-line/helpers/multi-country/faqs.helper";
import { CL_COUNTRY } from "@/apps/credit-line/types/cl_countries";
import { FAQ_PARAM } from "@/apps/credit-line/services/multi-country/faqs.service";
import { getDeviceLanguage } from "@/apps/credit-line/helpers/translations.helper";
import { loadMultipleAppTranslations } from "@/i18n/translationLoader";
import {
  createSettingsFaqService,
  FaqSettings,
  FaqCountryValues,
} from "@/apps/credit-line/helpers/settingsFaq.helper";

export default defineComponent({
  name: "FAQS",
  components: { Accordion },
  emits: ["redirectToCalculator", "cancelCreditLine"],
  props: {
    includeCcFaqs: Boolean,
    country: String as PropType<CL_COUNTRY>,
    faqParam: String,
    useFaqSettings: {
      type: Boolean,
      default: false,
    },
    faqSettings: {
      type: Object as PropType<FaqSettings | null>,
      default: null,
    },
    countryValues: {
      type: Object as PropType<FaqCountryValues | null>,
      default: null,
    },
  },
  setup(props) {
    const { t, locale, availableLocales } = useI18n();
    const screenTitle = ref<string>(t("HELP"));
    const sectionTitle = ref<string>(t("HELP"));
    // const faqsContent = computed(() => FaqsHelper(props.country as CL_COUNTRY));
    const defaultLocale = locale.value;

    onMounted(async () => {
      const dl = await getDeviceLanguage();
      const allowed = (availableLocales as unknown as string[]) || [];
      // Only set locale if it's one of the allowed locales; otherwise keep default
      // Cast to the precise type expected by vue-i18n's `locale.value`
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (locale as any).value = allowed.includes(dl)
        ? (dl as typeof locale.value)
        : defaultLocale;
      // Load translations for the new locale to avoid relying solely on fallback
      await loadMultipleAppTranslations(["credit-line", "shared"]);
    });

    onUnmounted(async () => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (locale as any).value = defaultLocale;
    });

    const currentCountry = computed(() =>
      getCountryFromI18nLocale(locale.value)
    );

    // Use settings-based FAQs if enabled and config is available, otherwise fall back to country-based FAQs
    const faqsContent = computed(() => {
      if (props.useFaqSettings && props.faqSettings) {
        return createSettingsFaqService(props.faqSettings);
      }
      return FaqsHelper(currentCountry.value);
    });

    const getFaqCategories = () => {
      // Use countryValues for placeholder substitution in translations
      const translationParams = props.countryValues || {};

      return faqsContent.value
        .getCategories(props.faqParam as FAQ_PARAM)
        .map((item) => {
          const questions = faqsContent.value.getQuestions(item.name);
          const answers = faqsContent.value.getAnswers(item.name);

          return {
            title: t(`CL_HELP_FAQS_CATEGORIES_TITLES.${item.name}`),
            category: item.name,
            questions: questions.map((x) => t(x, translationParams)),
            answers: answers.map((x) => t(x, translationParams)),
          };
        })
        .filter((x) =>
          !props.includeCcFaqs ? x.category !== "PAYJOY_CARD" : true
        );
    };

    const faqsCategories = computed(getFaqCategories);

    const iconType = computed(() =>
      faqsCategories.value.length !== 1 ? "plus_minus" : ""
    );

    const faqFiltered = computed(() => faqsCategories.value.length === 1);

    const accordionStyle = computed(() => {
      if (faqsCategories.value.length === 1) {
        return {
          color: "#19191B",
          fontFeatureSettings: "'clig' off, 'liga' off",
          fontFamily: "Inter",
          fontStyle: "normal",
          fontWeight: 600,
        };
      } else {
        return { color: "#2C7040" };
      }
    });

    const linkSettingsAvailable = ref<boolean>(false);

    const gtagEventOpenQuestion = (title: string): void => {
      logAnalyticsEvent(
        AnalyticEvents.CreditLine.HelpQuestionClicked,
        "Application",
        title
      );
    };

    const shouldShowWithdrawalCalculator = (faqCategory, index) => {
      return faqCategory.category === "PAYMENT" && index === 0;
    };

    const showCancelCreditLine = (faqCategory, index) => {
      return faqCategory.category === "ACCOUNT_CHANGES" && index === 1;
    };

    return {
      t,
      useAsset,
      iconType,
      screenTitle,
      faqFiltered,
      sectionTitle,
      accordionStyle,
      faqsCategories,
      linkSettingsAvailable,
      showCancelCreditLine,
      gtagEventOpenQuestion,
      shouldShowWithdrawalCalculator,
    };
  },
});
</script>

<style lang="scss" scoped>
@import "@/assets/scss/_constants.scss";
#item-link {
  color: $pj-color-green;
  text-decoration: none;
}
</style>
